/**
 * The outcomes that leave no trace in the change log.
 *
 * A change log records writes. So the two ways a task turn can end without
 * writing anything were, in the panel, rendered as nothing at all:
 *
 *   noop     you asked for something already true ("mark it done" — it's done)
 *   skipped  the agent named a task that no longer exists, or gave no title
 *
 * Both used to produce no toast, no chip and no panel row, after the UI had
 * already said it was working. "Nothing to do" and "it didn't work" looked
 * identical, and the first one is by far the more common.
 *
 * The counts live on the reply message instead of in the log, and the reply is
 * persisted — which is why reading them back out of the transcript makes them
 * survive a reload, where a piece of component state would not.
 */

/**
 * batch_id -> the write-nothing outcomes recorded on the reply that produced it.
 */
export function collectTaskOutcomeNotes(messages = []) {
    const map = new Map();
    for (const msg of messages) {
        if (msg?.role !== 'model' || !msg.task_batches?.length) continue;
        for (const batch of msg.task_batches) {
            const noop = batch?.noop || [];
            const skipped = batch?.skipped || [];
            if (!batch?.batch_id || (!noop.length && !skipped.length)) continue;
            map.set(batch.batch_id, {
                batch_id: batch.batch_id,
                noop,
                skipped,
                created_at: msg.timestamp || null,
            });
        }
    }
    return map;
}

const parsedAt = (value) => {
    const n = Date.parse(value);
    return Number.isNaN(n) ? 0 : n;
};

/**
 * The change log with the write-nothing turns folded back in.
 *
 * Merged onto their batch where the batch also wrote something; appended as a
 * display-only group where the whole batch wrote nothing — a group the server
 * will never return, because there is no row for it to return.
 */
export function mergeTaskOutcomeNotes(batches = [], notes, sessionId = null) {
    if (!notes || notes.size === 0) return batches;

    const merged = batches.map((b) => {
        const note = notes.get(b.batch_id);
        return note ? { ...b, noop: note.noop, skipped: note.skipped } : b;
    });

    const known = new Set(batches.map((b) => b.batch_id));
    let appended = false;
    for (const note of notes.values()) {
        if (known.has(note.batch_id)) continue;
        appended = true;
        merged.push({
            batch_id: note.batch_id,
            session_id: sessionId ?? null,
            message_index: null,
            source: 'ai',
            created_at: note.created_at,
            changes: [],
            summary: { created: 0, updated: 0, deleted: 0 },
            total: 0,
            active: 0,
            conflicted: false,
            noop: note.noop,
            skipped: note.skipped,
        });
    }

    // Only re-sort when something was appended. The server already returns these
    // newest-first, and its timestamps and the browser's are not written in the
    // same format — so a sort we don't need is a reorder we can't trust.
    if (!appended) return merged;
    return merged.sort((a, b) => parsedAt(b.created_at) - parsedAt(a.created_at));
}
